import {createSearchParams} from 'react-router-dom'
import {getRequest} from '../../services/base'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../context/global_context'
import {SET_USAGE_REPORT_LIST, UsageReportContext} from './context/context'
import UsageReportTable from './UsageReportTable'
import {getSessionStorageData} from '../../utils/SessionStorage'
import useDebouncedValue from '../../utils/common_function'
import Button from '../common/Button'
import {Col, DatePicker, Flex, Form, Row, Select, Tooltip, Typography} from 'antd'
import dayjs from 'dayjs'
import {useIntl} from 'react-intl'
import { notifyError } from '../../utils/notifications'

const UsageReport = () => {
  const intl = useIntl()

  const [state, dispatch] = useContext(UsageReportContext)
  const [, navDispatch] = useContext(NavContext)

  const [fromDate, setFromDate] = useState(dayjs())
  const [toDate, setToDate] = useState(dayjs())

  const [vehicle, setVehicle] = useState(null)
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [searchVehicles, setSearchVehicles] = useState('')
  const debouncedToSearchVehicles = useDebouncedValue(searchVehicles, 1000)

  const [selectedUsers, setSelectedUsers] = useState([])
  const [userOptions, setUserOptions] = useState([])
  const [searchUser, setSearchUser] = useState('')
  const debouncedToSearchUser = useDebouncedValue(searchUser, 1000)

  const [selectedActions, setSelectedActions] = useState([])
  const [selectedActionTypes, setSelectedActionTypes] = useState([])
  const [actionFilters, setActionFilters] = useState({})
  const [vehicleSearchFailed, setVehicleSearchFailed] = useState(false)
  const [userSearchFailed, setUserSearchFailed] = useState(false)

  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/report/usage/`)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.usageReport' }),
        sub_header: intl.formatMessage({ id: 'usageReport.subHeader' }),
        id: '',
        show_back_btn: true,
        dtc_details: {},
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getActionAndModelOptions()
  }, [])

  useEffect(() => {
    getVehiclesList(searchVehicles)
  }, [debouncedToSearchVehicles])

  useEffect(() => {
    getUserOptions(searchUser)
  }, [debouncedToSearchUser])

  const getVehiclesList = async (search = '') => {
    let res = await getRequest(`/api/vehicles/?search=${search}`)
    // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
    if (res?.status === false) {
      setVehicleSearchFailed(true)
      setVehicleOptions([])
      return
    }
    setVehicleSearchFailed(false)
    setVehicleOptions(
      (res?.results || []).map((vehicle) => ({
        value: vehicle.vehicle_id,
        label: vehicle.name,
      }))
    )
  }

  const getUserOptions = async (search = '') => {
    let res = await getRequest(`/api/users/?search=${search}&is_active=true`)
    // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
    if (res?.status === false) {
      setUserSearchFailed(true)
      setUserOptions([])
      return
    }
    setUserSearchFailed(false)
    setUserOptions(
      (res?.results || []).map((user) => ({
        value: user.id,
        label: user.email,
      }))
    )
  }

  const getActionAndModelOptions = async () => {
    let res = await getRequest(`/api/report/usage/options/`)
    // A one-shot load on mount, not a search, so a toast is the right signal.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'usageReport.filtersLoadError' }))
      return
    }
    setActionFilters(res?.data || {})
  }

  const actionOptions = useMemo(
    () =>
      (actionFilters?.activity_type || []).map((action) => ({
        value: action.id,
        label: action.name,
      })),
    [actionFilters]
  )

  const contentTypeOptions = useMemo(
    () =>
      (actionFilters?.content_type || []).map((contentType) => ({
        value: contentType.name,
        label: contentType.name,
      })),
    [actionFilters]
  )

  const buildUrl = () => {
    const params = new URLSearchParams({
      vehicle: vehicle || '',
      users: selectedUsers?.join(','),
      content_type: selectedActionTypes?.join(','),
      activity_type: selectedActions?.join(','),
      from_date: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : '',
      to_date: toDate ? dayjs(toDate).format('YYYY-MM-DD') : '',
    })
    return `/api/report/usage/?${params.toString()}`
  }

  const getReportsData = async (url = null) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'usageReport.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let response = await getRequest(currentUrl.current)
    setPagination(response?.pagination || {})
    dispatch({ type: SET_USAGE_REPORT_LIST, data: response?.results || [] })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // A 'loader' action resets `notification`, so notify after it, not before.
    if (response?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'usageReport.loadError' }))
    }
  }

  const onSubmit = () => {
    const requiredFields = [
      {
        required: fromDate,
        label: intl.formatMessage({ id: 'usageReport.fromDate' }),
        select: false,
      },
      {
        required: toDate,
        label: intl.formatMessage({ id: 'usageReport.toDate' }),
        select: false,
      },
      {
        required: vehicle,
        label: intl.formatMessage({ id: 'usageReport.selectMakeModel' }),
        select: true,
      },
    ]

    const missingField = requiredFields.find((field) => !field.required)
    if (missingField) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: missingField.select
            ? intl.formatMessage(
                { id: 'validation.selectField' },
                { label: missingField.label }
              )
            : intl.formatMessage(
                { id: 'validation.requiredField' },
                { label: missingField.label }
              ),
        },
      })
      return
    }

    currentUrl.current = buildUrl()
    getReportsData()
  }

  const dataSource = useMemo(() => {
    if (!state?.list?.length) return []
    return state?.list?.map((item, index) => ({
      ...item,
      key: `usage-report-${index}`,
      rowKey: `usage-report-${item.id}`,
      index: index,
    }))
  }, [state.list])

  return (
    <div className="page">
      <Row className="body">
        <Form className="full-width" onFinish={onSubmit}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <Form.Item style={{ marginBottom: 0 }}>
                <DatePicker
                  value={fromDate}
                  placeholder={`${intl.formatMessage({
                    id: 'usageReport.fromDate',
                  })} *`}
                  format="YYYY-MM-DD"
                  onChange={(date) => setFromDate(date)}
                  allowClear={false}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf('day')
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item style={{ marginBottom: 0 }}>
                <DatePicker
                  value={toDate}
                  placeholder={`${intl.formatMessage({
                    id: 'usageReport.toDate',
                  })} *`}
                  format="YYYY-MM-DD"
                  onChange={(date) => setToDate(date)}
                  allowClear={false}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf('day')
                  }
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Select
                  placeholder={`${intl.formatMessage({
                    id: 'usageReport.selectMakeModel',
                  })} *`}
                  options={vehicleOptions}
                  value={vehicle}
                  onChange={setVehicle}
                  onSearch={setSearchVehicles}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  filterOption={false}
                  notFoundContent={
                    vehicleSearchFailed ? (
                      <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                        {intl.formatMessage({ id: 'generic.searchFailed' })}
                      </Typography.Text>
                    ) : null
                  }
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder={intl.formatMessage({
                    id: 'usageReport.selectActions',
                  })}
                  options={actionOptions}
                  value={selectedActions}
                  onChange={setSelectedActions}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  maxTagCount="responsive"
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip
                      title={omittedValues.map((v) => v.label).join(', ')}
                    >
                      <span>+{omittedValues.length}</span>
                    </Tooltip>
                  )}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder={intl.formatMessage({
                    id: 'usageReport.selectUsers',
                  })}
                  options={userOptions}
                  value={selectedUsers}
                  onChange={setSelectedUsers}
                  onSearch={setSearchUser}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  filterOption={false}
                  notFoundContent={
                    userSearchFailed ? (
                      <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                        {intl.formatMessage({ id: 'generic.searchFailed' })}
                      </Typography.Text>
                    ) : null
                  }
                  maxTagCount="responsive"
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip
                      title={omittedValues.map((v) => v.label).join(', ')}
                    >
                      <span>+{omittedValues.length}</span>
                    </Tooltip>
                  )}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder={intl.formatMessage({
                    id: 'usageReport.selectReference',
                  })}
                  options={contentTypeOptions}
                  value={selectedActionTypes}
                  onChange={setSelectedActionTypes}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  maxTagCount="responsive"
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip
                      title={omittedValues.map((v) => v.label).join(', ')}
                    >
                      <span>+{omittedValues.length}</span>
                    </Tooltip>
                  )}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Flex justify="end" style={{ height: '100%' }}>
                <Button type="primary" htmlType="submit">
                  {intl.formatMessage({ id: 'usageReport.seeUsageReport' })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>

        <Flex style={{ width: '100%' }}>
          <UsageReportTable
            dataSource={dataSource}
            pagination={pagination}
            getReportsData={getReportsData}
          />
        </Flex>
      </Row>
    </div>
  )
}

export default UsageReport
